"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signOut, useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { useGetCallBackUrl } from "@/lib/helpers/hooks/useGetCallBackUrl";
import { sendOtp, signUp, signUpSocial } from "../services";
import { signUpSchema, SignUpPayloadType } from "../types";
import { getErrorMessage, isDefined } from "@/lib/utils";
import { useAuthAction } from "@/lib/auth/actions";
import { useSearchParams } from "next/navigation";
import Cookies from "js-cookie";
import { zodSchema } from "@/lib";
import { getActiveTermsDocument } from "@/lib/services/legal";
import { trackProductEvent } from "@/lib/analytics/product";

export function useSignUpForm() {
  const router = useRouter();
  const { data: session } = useSession();
  const { authenticateGoogle } = useAuthAction();
  const [loadingGoogleSignUp, setLoadingGoogleSignUp] = useState(false);
  const [loadingSignUp, setLoadingSignUp] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [timer, setTimer] = useState(0);
  const [activeTermsVersion, setActiveTermsVersion] = useState<string | null>(
    null,
  );
  const [activeTermsUrl, setActiveTermsUrl] = useState<string | null>(null);
  const { authenticate } = useAuthAction();

  const methods = useForm<SignUpPayloadType>({
    resolver: zodResolver(signUpSchema),
  });
  const { register, handleSubmit, formState } = methods;
  const searchParams = useSearchParams();

  const presetPhone = searchParams.get("phone-number");
  const presetUtmSource = searchParams.get("utm-source");
  const returnUrl = searchParams.get("return-url");
  const ONE_DAY = 1 * 24 * 60;

  if (returnUrl) {
    Cookies.set("returnUrl", encodeURI(returnUrl), {
      expires: ONE_DAY,
      httpOnly: false,
    });
  }

  useEffect(() => {
    const loadActiveTerms = async () => {
      const response = await getActiveTermsDocument();
      if (response?.version) {
        setActiveTermsVersion(response.version);
        setActiveTermsUrl(
          response.contentUrl || "https://centihq.com/terms-and-conditions",
        );
      }
    };

    loadActiveTerms();
  }, []);

  useEffect(() => {
    let timerId: any;
    function startTimer() {
      timerId = setTimeout(() => {
        setTimer((prevTimer) => (prevTimer > 0 ? prevTimer - 1 : 0));
      }, 1000);
    }
    startTimer();
    return () => clearTimeout(timerId);
  }, [timer]);

  useEffect(() => {
    if (
      presetPhone &&
      (presetUtmSource === "email" || presetUtmSource === "landing")
    ) {
      setShowGoogle(false);
      methods.setValue(
        "phone",
        presetPhone.includes("+") ? presetPhone : `+${presetPhone.trim()}`,
      );
    }
  }, [presetPhone]);

  const { errors } = formState;
  const callbackUrl = useGetCallBackUrl("/register/confirmation");

  type Response = { ok?: boolean; error?: string } | undefined;

  async function signUpWithGoogle() {
    setLoadingGoogleSignUp(true);
    await authenticateGoogle("signup", callbackUrl);
  }

  async function verifyOtp() {
    const phone = methods.getValues().phone;
    if (zodSchema.phone.safeParse(phone).success) {
      const res = await sendOtp({ phone });
      if (!res.error) {
        toast.success("OTP successfully sent!");
        setTimer(30);
      } else {
        toast.error(getErrorMessage(res));
      }
    } else {
      toast.error("Invalid phone number");
    }
  }

  const onSubmit = handleSubmit(async (data: SignUpPayloadType) => {
    if (!activeTermsVersion) {
      toast.error(
        "We couldn't load the latest Terms & Conditions. Please try again.",
      );
      return;
    }

    setLoadingSignUp(true);
    const res = await signUp({
      phone: data.phone,
      password: data.password,
      code: data.code,
      legalAcceptance: {
        accepted: data.acceptedTerms,
        version: activeTermsVersion,
        source: "signup",
      },
    });

    const phone = data.phone ?? presetPhone;
    localStorage.setItem("phone", phone);
    if (presetPhone) {
      trackProductEvent("Sign Up", {
        Method: "Phone",
        Status: "Invited",
      });
    } else {
      trackProductEvent("Sign Up", {
        Method: "Phone",
        Status: "Started",
      });
    }
    handleSignUpResponse(res);
  });

  async function handleSignUpResponse(res: any) {
    if (!res?.error) {
      await authenticate({ accessToken: res?.token });
      localStorage.removeItem("phone");
      if (presetUtmSource === "landing") {
        router.push(
          `/onboarding/user?name=${searchParams.get("name")}&email=${searchParams.get("email")}&utm-source=landing`,
        );
      } else {
        router.push("/onboarding/user");
      }
    } else {
      setLoadingSignUp(false);
      setLoadingGoogleSignUp(false);
      toast.error(getErrorMessage(res));
    }
  }

  async function handleGoogleSignUp(phone: string, isValid: boolean) {
    if (!isValid) {
      toast.error("Incomplete details");
    } else {
      setLoadingGoogleSignUp(true);
      const res = await signUpSocial({
        phone: phone,
        token: session?.idToken as string,
        provider: "google",
      });
      trackProductEvent("Sign Up", {
        Method: "Google",
        Status: "Started",
      });

      handleSignUpResponse(res);
    }
  }

  useEffect(() => {
    async function clearSession() {
      if (session) {
        await signOut();
      }
    }
    clearSession();
  }, []);

  // useEffect(() => {
  //   setTimeout(() => setShowGoogle(true), 100);
  // }, []);

  return {
    errors,
    methods,
    loadingGoogleSignUp,
    loadingSignUp,
    phoneNumberIsPreset: isDefined(presetPhone) && presetUtmSource === "email",
    onSubmit,
    register,
    signUpWithGoogle,
    handleGoogleSignUp,
    showGoogle,
    verifyOtp,
    timer,
    activeTermsVersion,
    activeTermsUrl,
  };
}
